// In-memory capture of the proctoring report handed back when a session
// ends (the /complete response the SDK attaches as `serverResponse` on
// session:completed / session:terminated). Quiz analytics reads it so the
// finish-time events can carry integrity data before the backend report is
// available. Keyed by the TEST session UID (the attempt), not the quiz -
// one quiz can have many attempts and a report only describes the attempt
// it was produced for. Deliberately in-memory: a page refresh loses it, at
// which point the result screen's backend-fetched proctoring_report takes
// over as the source.

interface ProctorReportSummary {
  integrityScore?: number;
  totalViolations?: number;
  sessionStatus?: string;
}

let storedTestSessionUID: string | null = null;
let storedSummary: ProctorReportSummary | null = null;

export const setProctorReport = (
  testSessionUID: string | null | undefined,
  serverResponse: unknown
): void => {
  const summary = (serverResponse as { summary?: ProctorReportSummary } | null)
    ?.summary;
  if (!testSessionUID || !summary) return;

  storedTestSessionUID = testSessionUID;
  storedSummary = {
    integrityScore: summary?.integrityScore,
    totalViolations: summary?.totalViolations,
    sessionStatus: summary?.sessionStatus
  };
};

// A new proctoring session invalidates whatever the previous attempt
// reported.
export const clearProctorReport = (): void => {
  storedTestSessionUID = null;
  storedSummary = null;
};

export const getProctorReportProps = (
  testSessionUID: string | null | undefined
): Record<string, number | string | undefined> => {
  if (
    !testSessionUID ||
    testSessionUID !== storedTestSessionUID ||
    !storedSummary
  ) {
    return {};
  }

  return {
    proctoring_integrity_score: storedSummary?.integrityScore,
    proctoring_total_violations: storedSummary?.totalViolations,
    proctoring_session_status: storedSummary?.sessionStatus
  };
};
